import { FiX, FiDownload } from 'react-icons/fi'

export default function CardPreviewModal({ card, onClose, onExport, previewData }) {
  // Use previewData if provided (for showing unsaved changes), otherwise use card data
  const displayData = previewData || card

  const issuedDate = displayData?.issuedDate ? new Date(displayData.issuedDate) : new Date(displayData?.createdAt)
  const formattedIssuedDate = issuedDate ? issuedDate.toLocaleDateString('en-GB') : 'N/A'

  const handleExport = (format) => {
    if (onExport) {
      onExport(displayData, format)
    }
  }

  const renderCardPreview = () => {
    const { cardData, layout, template, cardType } = displayData

    // ID Card base styles - professional CR80 size (85.6mm x 53.98mm)
    const baseStyles = {
      width: '100%',
      maxWidth: '360px',
      aspectRatio: '1.6',
      backgroundColor: layout?.backgroundColor || '#ffffff',
      color: layout?.textColor || '#000000',
      borderRadius: layout?.borderRadius || '8px',
      border: `2px solid ${layout?.accentColor || '#3B82F6'}`,
      fontFamily: layout?.fontFamily || 'Inter',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }

    if (cardType === 'business') {
      return (
        <div style={baseStyles} className="p-3 flex flex-col justify-between text-xs">
          {/* Logo and website */}
          <div className="flex items-center justify-between mb-1">
            {cardData.businessLogo?.url && (
              <img
                src={cardData.businessLogo.url}
                alt="Logo"
                className="h-5 w-auto object-contain"
              />
            )}
            {cardData.businessWebsite && (
              <div className="text-[10px] opacity-75 text-right">{cardData.businessWebsite}</div>
            )}
          </div>

          {/* Business name */}
          <div className="flex-1 flex flex-col justify-center">
            <h3 
              className="font-bold text-sm mb-0.5 leading-tight" 
              style={{ color: layout?.accentColor }}
            >
              {cardData.businessName}
            </h3>
            
            {/* Contact info */}
            <div className="space-y-0.5 text-[10px]">
              {cardData.businessWebsite && <div className="truncate">Website: {cardData.businessWebsite}</div>}
              {cardData.businessEmail && <div className="truncate">Email: {cardData.businessEmail}</div>}
              {cardData.businessPhone && <div>Phone: {cardData.businessPhone}</div>}
              {cardData.businessAddress && <div className="text-[9px] line-clamp-1">{cardData.businessAddress}</div>}
            </div>
          </div>

          {/* Footer - Issued date */}
          <div className="text-[9px] opacity-75 text-right border-t border-current pt-1 mt-1">
            Issued: {formattedIssuedDate}
          </div>
        </div>
      )
    } else {
      // Employee ID card - more compact
      return (
        <div style={baseStyles} className="p-3 flex gap-2">
          {/* Profile image section */}
          <div className="flex-shrink-0">
            {cardData.employeeProfileImage?.url ? (
              <img
                src={cardData.employeeProfileImage.url}
                alt="Profile"
                className="w-12 h-12 rounded object-cover border-2"
                style={{ borderColor: layout?.accentColor }}
              />
            ) : (
              <div
                className="w-12 h-12 rounded flex items-center justify-center text-lg"
                style={{ backgroundColor: layout?.accentColor + '20', color: layout?.accentColor }}
              >
                👤
              </div>
            )}
          </div>

          {/* Info section */}
          <div className="flex-1 flex flex-col justify-between min-w-0">
            {/* Name and position */}
            <div>
              <h3 
                className="font-bold text-xs leading-tight mb-0.5"
                style={{ color: layout?.accentColor }}
              >
                {cardData.employeeName}
              </h3>
              <div className="text-[10px] font-medium truncate">{cardData.employeePosition}</div>
            </div>

            {/* Contact and company */}
            <div className="text-[9px] space-y-0.5">
              {cardData.businessWebsite && <div className="truncate">Website: {cardData.businessWebsite}</div>}
              {cardData.employeeEmail && <div className="truncate">Email: {cardData.employeeEmail}</div>}
              {cardData.employeePhone && <div>{cardData.employeePhone}</div>}
              {cardData.businessName && <div className="font-medium truncate">{cardData.businessName}</div>}
            </div>

            {/* Issued date */}
            <div className="text-[8px] opacity-75 text-right border-t border-current pt-0.5 mt-0.5">
              {formattedIssuedDate}
            </div>
          </div>
        </div>
      )
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl max-h-[90vh] overflow-hidden shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{displayData.name}</h2>
            <p className="text-sm text-gray-600">
              {displayData.cardType === 'business' ? 'Business Card' : 'Employee Card'} • {displayData.template}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Preview */}
            <div className="flex justify-center">
              {renderCardPreview()}
            </div>

            {/* Card Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Card Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Type:</span>
                  <span className="ml-2 capitalize">{displayData.cardType}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Template:</span>
                  <span className="ml-2">{displayData.template}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Created:</span>
                  <span className="ml-2">{new Date(displayData.createdAt).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Issued:</span>
                  <span className="ml-2">{formattedIssuedDate}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Verification:</span>
                  <span className={`ml-2 ${displayData.verificationStatus === 'revoked' ? 'text-red-600' : 'text-emerald-600'}`}>
                    {displayData.verificationStatus === 'revoked' ? 'Revoked' : 'Active'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Verification ID:</span>
                  <span className="ml-2 break-all text-xs">{displayData.verificationId || 'N/A'}</span>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-700">Verification URL:</span>
                  {displayData.verificationUrl ? (
                    <a href={displayData.verificationUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline break-all text-xs">
                      {displayData.verificationUrl}
                    </a>
                  ) : (
                    <span className="ml-2">N/A</span>
                  )}
                </div>
              </div>
            </div>

            {displayData.qrCodeDataUrl && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Verification QR</h3>
                <div className="flex items-center justify-center">
                  <img src={displayData.qrCodeDataUrl} alt="QR code" className="w-28 h-28" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
          <button
            onClick={() => handleExport('pdf')}
            className="btn-secondary flex items-center gap-2"
          >
            <FiDownload className="w-4 h-4" />
            Export PDF
          </button>
          <button
            onClick={() => handleExport('png')}
            className="btn-secondary flex items-center gap-2"
          >
            <FiDownload className="w-4 h-4" />
            Export PNG
          </button>
        </div>
      </div>
    </div>
  )
}

// import { FiX, FiDownload } from 'react-icons/fi'

// export default function CardPreviewModal({ card, onClose, onExport, previewData }) {
//   // Use previewData if provided (for showing unsaved changes), otherwise use card data
//   const displayData = previewData || card

//   const issuedDate = displayData?.issuedDate ? new Date(displayData.issuedDate) : new Date(displayData?.createdAt)
//   const formattedIssuedDate = issuedDate ? issuedDate.toLocaleDateString('en-GB') : 'N/A'

//   const handleExport = (format) => {
//     if (onExport) {
//       onExport(displayData, format)
//     }
//   }

//   const renderCardPreview = () => {
//     const { cardData, layout, template, cardType } = displayData

//     // Base styles
//     const baseStyles = {
//       width: '100%',
//       maxWidth: '360px',
//       minWidth: '240px',
//       height: 'auto',
//       minHeight: '200px',
//       backgroundColor: layout?.backgroundColor || '#ffffff',
//       color: layout?.textColor || '#000000',
//       borderRadius: layout?.borderRadius || '8px',
//       border: `2px solid ${layout?.accentColor || '#3B82F6'}`,
//       fontFamily: layout?.fontFamily || 'Inter',
//       position: 'relative',
//       overflow: 'hidden'
//     }

//     if (cardType === 'business') {
//       return (
//         <div style={baseStyles} className="p-4 flex flex-col justify-between">
//           {/* Header with logo */}
//           <div className="flex items-center justify-between mb-2">
//             {cardData.businessLogo?.url && (
//               <img
//                 src={cardData.businessLogo.url}
//                 alt="Logo"
//                 className="h-8 w-auto object-contain"
//               />
//             )}
//             <div className="text-xs opacity-75">{cardData.businessWebsite}</div>
//           </div>

//           {/* Main content */}
//           <div className="flex-1 flex flex-col justify-center">
//             <h3 className="text-lg font-bold mb-1" style={{ color: layout?.accentColor }}>
//               {cardData.businessName}
//             </h3>
//             <div className="text-sm space-y-1">
//               {cardData.businessEmail && <div>{cardData.businessEmail}</div>}
//               {cardData.businessPhone && <div>{cardData.businessPhone}</div>}
//               {cardData.businessAddress && <div className="text-xs">{cardData.businessAddress}</div>}
//             </div>
//           </div>

//           {/* Footer */}
//           <div className="text-xs opacity-75 text-right">
//             {cardData.businessDescription}
//             {cardData.businessWebsite && <div>{cardData.businessWebsite}</div>}
//             <div>Issued: {formattedIssuedDate}</div>
//           </div>
//         </div>
//       )
//     } else {
//       // Employee card
//       return (
//         <div style={baseStyles} className="p-4 flex">
//           {/* Profile image */}
//           <div className="flex-shrink-0 mr-4">
//             {cardData.employeeProfileImage?.url ? (
//               <img
//                 src={cardData.employeeProfileImage.url}
//                 alt="Profile"
//                 className="w-16 h-16 rounded-full object-cover border-2"
//                 style={{ borderColor: layout?.accentColor }}
//               />
//             ) : (
//               <div
//                 className="w-16 h-16 rounded-full flex items-center justify-center text-2xl"
//                 style={{ backgroundColor: layout?.accentColor + '20', color: layout?.accentColor }}
//               >
//                 👤
//               </div>
//             )}
//           </div>

//           {/* Content */}
//           <div className="flex-1">
//             <h3 className="text-lg font-bold mb-1" style={{ color: layout?.accentColor }}>
//               {cardData.employeeName}
//             </h3>
//             <div className="text-sm font-medium mb-2">{cardData.employeePosition}</div>
//             <div className="text-xs space-y-1">
//               {cardData.employeeEmail && <div>{cardData.employeeEmail}</div>}
//               {cardData.employeePhone && <div>{cardData.employeePhone}</div>}
//               {cardData.businessName && <div className="font-medium">{cardData.businessName}</div>}
//               {cardData.businessWebsite && <div>{cardData.businessWebsite}</div>}
//             </div>
//             {cardData.employeeBio && (
//               <div className="text-xs mt-2 opacity-75 line-clamp-2">
//                 {cardData.employeeBio}
//               </div>
//             )}
//             <div className="text-xs mt-2 opacity-75">Issued: {formattedIssuedDate}</div>
//           </div>
//         </div>
//       )
//     }
//   }

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
//       <div className="bg-white rounded-lg w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl max-h-[90vh] overflow-hidden shadow-lg">
//         {/* Header */}
//         <div className="flex items-center justify-between p-6 border-b border-gray-200">
//           <div>
//             <h2 className="text-xl font-semibold text-gray-900">{displayData.name}</h2>
//             <p className="text-sm text-gray-600">
//               {displayData.cardType === 'business' ? 'Business Card' : 'Employee Card'} • {displayData.template}
//             </p>
//           </div>
//           <button
//             onClick={onClose}
//             className="p-2 hover:bg-gray-100 rounded-lg"
//           >
//             <FiX className="w-5 h-5" />
//           </button>
//         </div>

//         {/* Preview */}
//         <div className="p-6">
//           <div className="flex justify-center mb-6">
//             <div className="transform scale-110">
//               {renderCardPreview()}
//             </div>
//           </div>

//           {/* Card Details */}
//           <div className="bg-gray-50 rounded-lg p-4 mb-6">
//             <h3 className="font-semibold text-gray-900 mb-3">Card Details</h3>
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
//               <div>
//                 <span className="font-medium text-gray-700">Type:</span>
//                 <span className="ml-2 capitalize">{displayData.cardType}</span>
//               </div>
//               <div>
//                 <span className="font-medium text-gray-700">Template:</span>
//                 <span className="ml-2">{displayData.template}</span>
//               </div>
//               <div>
//                 <span className="font-medium text-gray-700">Created:</span>
//                 <span className="ml-2">{new Date(displayData.createdAt).toLocaleDateString()}</span>
//               </div>
//               <div>
//                 <span className="font-medium text-gray-700">Issued:</span>
//                 <span className="ml-2">{formattedIssuedDate}</span>
//               </div>
//               <div>
//                 <span className="font-medium text-gray-700">Verification:</span>
//                 <span className={`ml-2 ${displayData.verificationStatus === 'revoked' ? 'text-red-600' : 'text-emerald-600'}`}>
//                   {displayData.verificationStatus === 'revoked' ? 'Revoked' : 'Active'}
//                 </span>
//               </div>
//               <div>
//                 <span className="font-medium text-gray-700">Verification ID:</span>
//                 <span className="ml-2 break-all text-xs">{displayData.verificationId || 'N/A'}</span>
//               </div>
//               <div className="col-span-2">
//                 <span className="font-medium text-gray-700">Verification URL:</span>
//                 {displayData.verificationUrl ? (
//                   <a href={displayData.verificationUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline break-all text-xs">
//                     {displayData.verificationUrl}
//                   </a>
//                 ) : (
//                   <span className="ml-2">N/A</span>
//                 )}
//               </div>
//             </div>
//           </div>

//           {displayData.qrCodeDataUrl && (
//             <div className="bg-gray-50 rounded-lg p-4 mb-6">
//               <h3 className="font-semibold text-gray-900 mb-3">Verification QR</h3>
//               <div className="flex items-center justify-center">
//                 <img src={displayData.qrCodeDataUrl} alt="QR code" className="w-28 h-28" />
//               </div>
//             </div>
//           )}
//         </div>

//         {/* Actions */}
//         <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
//           <button
//             onClick={() => handleExport('pdf')}
//             className="btn-secondary flex items-center gap-2"
//           >
//             <FiDownload className="w-4 h-4" />
//             Export PDF
//           </button>
//           <button
//             onClick={() => handleExport('png')}
//             className="btn-secondary flex items-center gap-2"
//           >
//             <FiDownload className="w-4 h-4" />
//             Export PNG
//           </button>
//         </div>
//       </div>
//     </div>
//   )
// }